import { Injectable } from '@angular/core';
import { IPhoneNumberService } from '@smsamiinc/invoice'; // Can import from any plugin
import { parsePhoneNumber, AsYouType, CountryCode } from 'libphonenumber-js';

import { environment } from '../../../environments/environment';

/**
 * Phone number formatting service for POS application
 * Uses libphonenumber-js for robust international phone number handling
 *
 * Default country is configured in environment.locale.defaultCountry (Pakistan: 'PK')
 * This allows automatic conversion of local numbers to international format:
 * - Input: '0333 5700762' → Output: '+923335700762' (E.164)
 * - Input: '+92 333 5700762' → Output: '+923335700762' (E.164)
 *
 * @see https://gitlab.com/catamphetamine/libphonenumber-js
 */
@Injectable({ providedIn: 'root' })
export class PhoneNumberService implements IPhoneNumberService {
    private readonly defaultCountry: CountryCode = this.getDefaultCountry();

    /**
     * Get default country from environment or fallback to Pakistan
     */
    private getDefaultCountry(): CountryCode {
        const env = environment as { locale?: { defaultCountry?: string } };
        return (env.locale?.defaultCountry as CountryCode) || 'PK';
    }

    /**
     * Convert phone number to E.164 international format
     * E.164 format: +[country code][number] (e.g., +12025551234)
     */
    toE164(phoneNumber: string, country?: string): string | null {
        if (!phoneNumber) {
            return null;
        }

        try {
            const countryCode = (country || this.defaultCountry) as CountryCode;
            const parsed = parsePhoneNumber(phoneNumber, countryCode);

            if (parsed?.isValid()) {
                return parsed.number; // E.164 format with +
            }
        } catch {
            // Invalid phone number format - silently fail
        }

        return null;
    }

    /**
     * Convert E.164 phone number to national format
     * National format: formatted for display in the phone's country (e.g., (202) 555-1234)
     */
    toNational(e164Phone: string): string {
        if (!e164Phone) {
            return '';
        }

        try {
            const parsed = parsePhoneNumber(e164Phone);

            if (parsed?.isValid()) {
                return parsed.formatNational();
            }
        } catch {
            // Invalid E.164 phone number - silently fail
        }

        // Fallback: return as-is
        return e164Phone;
    }

    /**
     * Validate phone number
     * Checks if the phone number is valid for the specified country
     */
    isValid(phoneNumber: string, country?: string): boolean {
        if (!phoneNumber) {
            return false;
        }

        try {
            const countryCode = (country || this.defaultCountry) as CountryCode;
            const parsed = parsePhoneNumber(phoneNumber, countryCode);

            return parsed ? parsed.isValid() : false;
        } catch {
            // Invalid format
            return false;
        }
    }

    /**
     * Normalize phone number for search/comparison
     * Returns E.164 format without the + prefix (digits only with country code)
     * E.g., "+12025551234" → "12025551234"
     */
    normalize(phoneNumber: string): string {
        if (!phoneNumber) {
            return '';
        }

        try {
            const parsed = parsePhoneNumber(phoneNumber, this.defaultCountry);

            if (parsed?.isValid()) {
                // Remove + from E.164 format
                return parsed.number.replace(/^\+/, '');
            }
        } catch {
            // Invalid format, return cleaned digits
        }

        // Fallback: return only digits
        return phoneNumber.replace(/\D/g, '');
    }

    /**
     * Convert E.164 format to local 0-prefixed format
     * Example: "+923001234567" → "03001234567"
     */
    toNationalFormat(e164: string): string {
        const match = e164.match(/^\+(\d{1,2})(.*)$/); // match +92, +1, etc.
        if (!match) return e164;
        return '0' + match[2];
    }

    /**
     * Format phone number as user types
     * Provides real-time formatting feedback as the user enters a phone number
     */
    formatAsYouType(phoneNumber: string, country?: string): string {
        if (!phoneNumber) {
            return '';
        }

        try {
            const countryCode = (country || this.defaultCountry) as CountryCode;
            const formatter = new AsYouType(countryCode);

            return formatter.input(phoneNumber);
        } catch {
            // Fallback: return as-is
            return phoneNumber;
        }
    }

    /**
     * Format a phone number for display (legacy method)
     * @deprecated Use formatAsYouType instead
     */
    formatPhoneNumber(phoneNumber: string, countryCode = 'US'): string {
        return this.formatAsYouType(phoneNumber, countryCode);
    }

    /**
     * Validate a phone number (legacy method)
     * @deprecated Use isValid instead
     */
    validatePhoneNumber(phoneNumber: string, countryCode = 'US'): boolean {
        return this.isValid(phoneNumber, countryCode);
    }

    /**
     * Parse a phone number to extract components (legacy method)
     * @deprecated Use normalize or toE164 instead
     */
    parsePhoneNumber(phoneNumber: string): {
        countryCode?: string;
        areaCode?: string;
        number: string;
    } {
        if (!phoneNumber) {
            return { number: '' };
        }

        const cleaned = phoneNumber.replace(/\D/g, '');

        if (cleaned.length === 10) {
            return {
                areaCode: cleaned.slice(0, 3),
                number: cleaned.slice(3),
            };
        } else if (cleaned.length === 11 && cleaned[0] === '1') {
            return {
                countryCode: '1',
                areaCode: cleaned.slice(1, 4),
                number: cleaned.slice(4),
            };
        }

        return { number: cleaned };
    }
}
